import React, { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getAssetDtoToAsset } from './asset.converters';
import { CustomMutationOptions } from '../common.types';
import {
    AssetsService,
    CreateAssetReqDto,
    CreateAssetResDto,
} from '../../generated';

interface Filter {
    search?: string;
}

export const useDebouncedAssets = (search: string, debounce: number = 1000) => {
    const [debouncedParams, setDebouncedParams] = React.useState<
        Filter | undefined
    >();

    React.useEffect(() => {
        const handler = setTimeout(() => {
            const filter: Filter = {};
            if (search.length > 1) filter.search = search;
            setDebouncedParams(
                Object.keys(filter).length > 0 ? filter : undefined
            );
        }, debounce);

        return () => {
            clearTimeout(handler);
        };
    }, [search, debounce]);

    const { data, ...other } = useQuery({
        queryFn: async () => {
            const params = debouncedParams as Filter;

            const response = await AssetsService.getAssets({
                query: params.search ? { text_search: params.search } : {},
            });

            return response.data;
        },
        queryKey: [debouncedParams],
        keepPreviousData: true,
        staleTime: debounce,
        enabled: !!debouncedParams,
    });

    const parsedData = useMemo(
        () =>
            debouncedParams && data?.items
                ? data?.items.map(getAssetDtoToAsset)
                : undefined,
        [data]
    );

    return {
        data: parsedData,
        ...other,
        isLoading: other.isFetching,
    };
};

export const useCreateAsset = (
    options?: CustomMutationOptions<
        Omit<CreateAssetReqDto, 'type'>,
        CreateAssetResDto
    >
) => {
    return useMutation({
        mutationFn: async data => {
            const response = await AssetsService.createAsset({
                body: {
                    ...data,
                    type: 'vehicle',
                },
            });

            if (!response.data) throw new Error('Failed to create asset');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // Invalidate assets list
            options?.onSuccess?.(...args);
        },
    });
};
